<template>
  <nav class="case-industry-nav" aria-label="industry">
    <div class="case-industry-nav__cats">
      <router-link
        v-if="mode === 'case'"
        :to="basePath + 'all/'"
        class="case-industry-nav__cat"
        :class="{ 'is-active': isAllActive }"
      >
        {{ allLabel }}
      </router-link>
      <router-link
        v-for="cat in categories"
        :key="cat.id"
        :to="basePath + categoryRouteSlug(cat) + '/'"
        class="case-industry-nav__cat"
        :class="{ 'is-active': isActiveCategory(cat) }"
      >
        {{ categoryLabel(cat) }}
      </router-link>
    </div>
    <div class="case-industry-nav__subs" v-if="activeSubs.length">
      <router-link
        :to="basePath + activeRouteSlug + '/'"
        class="case-industry-nav__sub"
        :class="{ 'is-active': !activeSubSlug && !activeSubId }"
      >
        {{ allLabel }}
      </router-link>
      <router-link
        v-for="sub in activeSubs"
        :key="sub.id"
        :to="subItemLink(sub)"
        class="case-industry-nav__sub"
        :class="{ 'is-active': isActiveSubItem(sub) }"
      >
        {{ subLabel(sub) }}
      </router-link>
    </div>
  </nav>
</template>

<script>
import { listSolutionCategory, listSolutionItem } from '@/utils/http'
import { unwrapRows } from '@/utils/content'
import { SOLUTION_NAV, APPLICATION_NAV } from '@/constants/hiraysHomeStatic'

const SLUG_ALIASES = {
  batteries: 'battery',
  hvac: 'hvac-refrigeration',
  medical: 'medical-devices',
  aerospace: 'defense-aerospace',
  'advanced-materials': 'new-materials',
  'wires-connectors': 'wire-connector',
  others: 'other',
  semiconductors: 'semiconductor'
}

const REVERSE_SLUG_ALIASES = Object.keys(SLUG_ALIASES).reduce((acc, key) => {
  acc[SLUG_ALIASES[key]] = key
  return acc
}, {})

export default {
  name: 'CaseIndustryNav',
  props: {
    mode: { type: String, default: 'case' },
    activeSlug: { type: String, default: '' },
    activeSubSlug: { type: String, default: '' },
    activeSubId: { type: [String, Number], default: null }
  },
  data() {
    return { categories: [], subItems: [], subItemsBySlug: {} }
  },
  computed: {
    basePath() {
      return this.mode === 'case' ? '/case/' : '/solution/'
    },
    categoryType() {
      return this.mode === 'case' ? 1 : 0
    },
    navGroup() {
      return this.mode === 'case' ? 'applicationNav' : 'solutionNav'
    },
    staticNav() {
      return this.mode === 'case' ? APPLICATION_NAV : SOLUTION_NAV
    },
    activeRouteSlug() {
      return this.activeSlug || ''
    },
    activeSubs() {
      const cat = this.categories.find(c => this.isActiveCategory(c))
      if (!cat) return []
      return this.getSubItems(cat.id)
    },
    allLabel() {
      return this.$i18n.locale === 'zh' ? '\u5168\u90E8' : 'All'
    },
    isAllActive() {
      return this.mode === 'case' && (!this.activeSlug || this.activeSlug === 'all')
    }
  },
  created() {
    this.load()
  },
  methods: {
    reloadCmsData() {
      this.load()
    },
    normalizeSlug(slug) {
      return SLUG_ALIASES[slug] || slug
    },
    sortCategories(cats) {
      return (cats || []).slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || Number(a.id) - Number(b.id))
    },
    categoryRouteSlug(cat) {
      return REVERSE_SLUG_ALIASES[cat.categorySlug] || cat.categorySlug
    },
    isActiveCategory(cat) {
      if (this.isAllActive) return false
      const routeSlug = this.categoryRouteSlug(cat)
      return this.activeSlug === routeSlug || this.activeSlug === cat.categorySlug
    },
    categoryLabel(cat) {
      return this.$navItemLabel(this.navGroup, cat)
    },
    subLabel(sub) {
      return this.$navItemLabel('solutionSubNav', sub)
    },
    subItemLink(sub) {
      const cat = this.categories.find(c => this.isActiveCategory(c))
      if (!cat) return this.basePath
      const slug = this.categoryRouteSlug(cat)
      const prefix = this.basePath + slug + '/'
      // Case chips filter the grid; do not jump to a sparse detail shell
      if (this.mode === 'case') {
        return prefix + (sub.itemSlug || String(sub.id)) + '/'
      }
      if (sub.picUrl) {
        return prefix + sub.id + '.html'
      }
      return prefix + (sub.itemSlug || String(sub.id)) + '/'
    },
    isActiveSubItem(sub) {
      if (this.activeSubSlug && this.activeSubSlug === sub.itemSlug) return true
      if (this.activeSubId && String(this.activeSubId) === String(sub.id)) return true
      return false
    },
    getSubItems(catId) {
      const direct = this.subItems
        .filter(item => item.categoryId === catId)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      if (direct.length) return direct
      const cat = this.categories.find(c => c.id === catId)
      if (!cat) return []
      const routeSlug = this.categoryRouteSlug(cat)
      return this.subItemsBySlug[cat.categorySlug] ||
        this.subItemsBySlug[routeSlug] ||
        this.subItemsBySlug[this.normalizeSlug(routeSlug)] ||
        []
    },
    async load() {
      try {
        const requests = [
          listSolutionCategory({ categoryType: this.categoryType, status: 1, pageNum: 1, pageSize: 50 }, this.$i18n.locale),
          listSolutionItem({ status: 1, pageNum: 1, pageSize: 500 }, this.$i18n.locale)
        ]
        if (this.mode === 'case') {
          requests.push(listSolutionCategory({ categoryType: 0, status: 1, pageNum: 1, pageSize: 50 }, this.$i18n.locale))
        }
        const results = await Promise.all(requests)
        const cats = unwrapRows(results[0])
        const allSubItems = unwrapRows(results[1])
        if (cats.length) {
          this.categories = this.sortCategories(cats)
          this.subItems = allSubItems
          this.subItemsBySlug = {}
          if (this.mode === 'case' && results[2]) {
            unwrapRows(results[2]).forEach(sc => {
              const items = allSubItems
                .filter(item => item.categoryId === sc.id)
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
              const routeSlug = REVERSE_SLUG_ALIASES[sc.categorySlug] || sc.categorySlug
              this.subItemsBySlug[sc.categorySlug] = items
              this.subItemsBySlug[routeSlug] = items
              const normalized = SLUG_ALIASES[routeSlug]
              if (normalized) this.subItemsBySlug[normalized] = items
            })
          }
        } else {
          this.useStaticFallback()
        }
      } catch (e) {
        this.useStaticFallback()
      }
    },
    useStaticFallback() {
      this.categories = []
      this.subItems = []
      this.subItemsBySlug = {}
    }
  }
}
</script>
